<template>
    <transition name="fade">
        <div
            v-if="!dismissed"
            class="flex flex-col p-6 items-center justify-center gap-2 rounded-lg bg-white shadow-md mt-6"
        >
            <LoadingInline v-if="isLoading" :loading="isLoading" />
            <div
                v-if="!isLoading"
                class="flex w-full justify-between items-center"
            >
                <div class="flex items-center">
                    <icon icon="flame" />
                    <span
                        class="text-base lg:text-lg font-medium text-slate-800 ml-2"
                    >
                        Get started with My10x!
                    </span>
                </div>
                <span class="text-slate-800 font-medium text-xs lg:text-sm">
                    {{ progress }}% completed
                </span>
            </div>
            <div
                v-if="!isLoading"
                class="flex w-full py-6 pb-2 flex-col items-start gap-4"
            >
                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': open > 0 || closed > 0,
                            'bg-orange-50': open === 0 && closed === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="
                                open > 0 || closed > 0
                                    ? 'check-green'
                                    : 'credit-card'
                            "
                            :class="{
                                'text-green-500': open > 0 || closed > 0,
                                'text-orange': open === 0 && closed === 0,
                            }"
                        />
                    </div>
                    <div>
                        <h2 class="text-base text-slate-800 font-medium">
                            Add your credit cards
                        </h2>
                        <p class="text-sm font-normal text-slate-500 prose">
                            Add your cards to stay organized and unlock
                            personalized insights tailored to your wallet.
                        </p>
                    </div>
                    <button
                        v-if="!isLoading && open === 0 && closed === 0"
                        @click="redirectToWallet"
                        class="flex p-2 px-4 justify-center items-center gap-2 rounded-md bg-orange shadow-md text-white ml-auto text-xs lg:text-sm"
                    >
                        Start
                    </button>
                </div>

                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': points > 0,
                            'bg-orange-50': points === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="points > 0 ? 'check-green' : 'points'"
                            :class="{
                                'text-green-500': points > 0,
                                'text-orange': points === 0,
                            }"
                        />
                    </div>
                    <div>
                        <h2 class="text-base text-slate-800 font-medium">
                            Start tracking your points
                        </h2>
                        <p class="text-sm font-normal text-slate-500 prose">
                            Sync loyalty accounts or add balances manually to
                            track your points and miles in one place.
                        </p>
                    </div>
                    <button
                        v-if="!isLoading && points === 0"
                        @click="redirectToPoints"
                        class="flex p-2 px-4 justify-center items-center gap-2 rounded-md bg-orange shadow-md text-white ml-auto text-xs lg:text-sm"
                    >
                        Start
                    </button>
                </div>

                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': creditScore > 0,
                            'bg-orange-50': creditScore === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="
                                creditScore > 0 ? 'check-green' : 'credit-score'
                            "
                            :class="{
                                'text-green-500': creditScore > 0,
                                'text-orange': creditScore === 0,
                            }"
                        />
                    </div>
                    <div>
                        <h2 class="text-base text-slate-800 font-medium">
                            Start tracking your credit
                        </h2>
                        <p class="text-sm text-slate-500 prose">
                            Monitor your credit to track changes over time and
                            make smarter rewards decisions.
                        </p>
                    </div>
                    <button
                        v-if="!isLoading && creditScore === 0"
                        @click="openCreditScoreModal"
                        class="flex p-2 px-4 justify-center items-center gap-2 rounded-md bg-orange shadow-md text-white ml-auto text-xs lg:text-sm"
                    >
                        Start
                    </button>
                </div>

                <div class="flex items-center gap-3 self-stretch py-2">
                    <div
                        :class="{
                            'bg-emerald-50': hasStartedCourse,
                            'bg-orange-50': !hasStartedCourse,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="hasStartedCourse ? 'check-green' : 'courses'"
                            :class="{
                                'text-green-500': hasStartedCourse,
                                'text-orange': !hasStartedCourse,
                            }"
                        />
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <div class="text-base text-slate-800 font-medium">
                            Complete our course
                        </div>
                        <div class="text-sm font-normal text-slate-500 prose">
                            Learn the fundamentals to confidently maximize your
                            rewards and achieve your travel goals.
                        </div>
                    </div>
                    <button
                        v-if="!isLoading && !hasStartedCourse"
                        @click="redirectToCourse"
                        class="flex p-2 px-4 justify-center items-center gap-2 rounded-md bg-orange shadow-md text-white ml-auto text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                    >
                        Start
                    </button>
                </div>
                <button
                    v-if="progress === 100"
                    :disabled="loading"
                    class="bg-white text-slate-800 shadow-md font-medium text-sm py-2.5 px-4 rounded border border-slate-200 ml-auto focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                    @click="handleDismissAction"
                >
                    Done, hide this
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState, mapActions } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import { useCreditScoreStore } from '@/store/creditscore';
import { useCoursesStore } from '@/store/courses';
import Icon from '../../ui/icon/icon.vue';
import LoadingInline from '@/components/ui/loading-inline.vue';
import CreditScoreModal from '@/components/creditscore/modals/credit-score.vue';

export default defineComponent({
    components: { LoadingInline, Icon, CreditScoreModal },
    data() {
        return {
            isVisible: true,
        };
    },
    computed: {
        ...mapState(useCoursesStore, {
            courseProgress: (state) => state.progress,
            loadingCourse: (state) => state.loadingCourse,
        }),
        ...mapState(useDashboardStore, [
            'user',
            'open',
            'closed',
            'points',
            'loading',
        ]),
        ...mapState(useCreditScoreStore, [
            'userScoreData',
            'loadingUserCreditScore',
            'showCreditScoreModal',
        ]),
        isLoading() {
            return this.loadingCourse || this.loadingUserCreditScore;
        },
        creditScore() {
            return this.userScoreData?.latest_score || 0;
        },
        progress() {
            let progress = 0;
            if (this.open > 0 || this.closed > 0) progress += 1;
            if (this.points > 0) progress += 1;
            if (this.creditScore > 0) progress += 1;
            if (this.hasStartedCourse) progress += 1;
            return (progress / 4) * 100;
        },
        hasStartedCourse() {
            return (this.courseProgress || 0) > 0;
        },
        dismissed() {
            return this.user?.start_guide_completed;
        },
    },
    methods: {
        ...mapActions(useCoursesStore, ['getFirstCourse']),
        ...mapActions(useDashboardStore, ['updateUserPreference']),
        ...mapActions(useCreditScoreStore, ['openCreditScoreModal']),
        redirectToWallet() {
            window.location.href = '/wallet';
        },
        redirectToPoints() {
            window.location.href = '/mypoints';
        },
        redirectToCourse() {
            window.location.href = '/course';
        },
        handleDismissAction() {
            this.dismissAction();
        },
        dismissAction() {
            this.updateUserPreference('start_guide_completed_at');
        },
    },
    mounted() {
        this.getFirstCourse();
    },
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity duration-500;
}
.fade-enter, .fade-leave-to /* .fade-leave-active en <2.1.8 */ {
    @apply opacity-0;
}
</style>
